import { useMemo } from 'react';
import { LayoutCarousel, Tooltip } from '@silverfort/sf-components';
import Avatar from '../../../../components/Avatar/Avatar';
import { splitArrayIntoBatches } from '../../../../utils';

const DEFAULT_BATCH_SIZE = 3;

type ListItem = {
    name: string;
    guid: string;
    count: number;
};

interface TopDailyMfaChartProps {
    title: string;
    list: ListItem[];
    isLoading: boolean;
    showAvatar?: boolean;
    className?: string;
}

const MfaRow = ({ item, showAvatar }: { item: ListItem; showAvatar?: boolean }) => {
    return (
        <div className="flex flex-row items-center justify-between flex-1 bg-white h-10 w-full p-1 rounded-lg">
            <div className="flex-1 flex items-center gap-2">
                <Avatar name={item.name} show={showAvatar} />
                <div className="flex-1 text-13 text-sf-gray-800">{item.name}</div>
            </div>
            <div className="text-12 bg-sf-white border border-sf-gray-100 rounded-[4px] px-2">
                <Tooltip placement='top' title='Hits'>
                    <>{item.count.toLocaleString()}</>
                </Tooltip>
            </div>
        </div>
    );
};

const LoadingRow = ({ showAvatar }: { showAvatar?: boolean }) => (
    <div className="flex flex-row items-center justify-between flex-1 bg-white h-10 w-full p-1 rounded-lg">
        <div className="flex-1 flex items-center gap-2">
            {showAvatar && <div className="w-8 h-8 rounded-full bg-sf-gray-200 animate-pulse" />}
            <div className="h-4 w-32 bg-sf-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex items-center">
            <div className="h-6 w-16 bg-sf-gray-200 rounded animate-pulse" />
        </div>
    </div>
);

const NoDataRow = ({ showAvatar, isFirstRow }: { showAvatar?: boolean; isFirstRow?: boolean }) => (
    <div className="flex flex-row items-center justify-between flex-1 bg-white h-10 w-full p-1 rounded-lg">
        <div className="flex-1 flex items-center gap-2">
            {showAvatar && <div className="w-8 h-8 rounded-full bg-sf-gray-200" />}
            <div className="text-13 text-sf-gray-500">{isFirstRow ? 'No data' : ''}</div>
        </div>
        <div className="flex items-center">
            <div className="h-6 w-16 bg-sf-gray-200 rounded" />
        </div>
    </div>
);

const TopDailyMfaChart = ({ title, list, isLoading, showAvatar = true, className }: TopDailyMfaChartProps) => {
    const batches = useMemo(() => splitArrayIntoBatches(list, DEFAULT_BATCH_SIZE), [list]);

    const pages = useMemo(() => {
        if (isLoading) {
            return [
                <div key="loading" className="flex flex-1 flex-col w-full gap-2 items-start">
                    {Array.from({ length: DEFAULT_BATCH_SIZE }, (_, index) => (
                        <LoadingRow key={index} showAvatar={showAvatar} />
                    ))}
                </div>
            ];
        }

        if (list.length === 0) {
            return [
                <div key="no-data" className="flex flex-1 flex-col w-full gap-2 items-start">
                    {Array.from({ length: DEFAULT_BATCH_SIZE }, (_, index) => (
                        <NoDataRow key={index} showAvatar={showAvatar} isFirstRow={index === 0} />
                    ))}
                </div>
            ];
        }

        const pagesArray: React.ReactNode[] = batches.map((batch, index) => (
            <div key={index} className="flex flex-1 flex-col w-full gap-2 items-start">
                {batch.map((item) => (
                    <MfaRow key={item.guid} item={item} showAvatar={showAvatar} />
                ))}
            </div>
        ));
        return pagesArray;
    }, [isLoading, list, batches, showAvatar]);

    return (
        <LayoutCarousel
            title={title}
            pages={pages}
            className={className}
            contentClassName="flex flex-1 flex-col justify-center px-6 flex-shrink-0 h-full min-h-[150px]"
        />
    );
};

export default TopDailyMfaChart;
